import type { NutritionEU, NutritionEUUnit } from "api-types";
import {
  type NutritionEUColumn,
  type NutritionRounding,
  nutritionEUColumns,
  nutritionRounding,
} from "menu-filtering";

const MISSING = "–";

type Nutrient =
  | "fat"
  | "saturates"
  | "carbohydrate"
  | "sugars"
  | "fibre"
  | "protein"
  | "salt";

// Below energy, in the order UK labelling sets out.
export const NUTRIENT_ROWS: { nutrient: Nutrient; ofWhich?: boolean }[] = [
  { nutrient: "fat" },
  { nutrient: "saturates", ofWhich: true },
  { nutrient: "carbohydrate" },
  { nutrient: "sugars", ofWhich: true },
  { nutrient: "fibre" },
  { nutrient: "protein" },
  { nutrient: "salt" },
];

export type NutritionTableColumn = {
  key: "per100" | "perPortion";
  values: NutritionEUColumn;
};

/** The table's columns, leaving out any with no value in any row. */
export const nutritionTableColumns = (
  nutrition: NutritionEU
): NutritionTableColumn[] => {
  const { per100, perPortion } = nutritionEUColumns(nutrition);
  const columns: NutritionTableColumn[] = [
    { key: "per100", values: per100 },
    { key: "perPortion", values: perPortion },
  ];
  return columns.filter(({ values }) =>
    [
      values.energyKcal,
      ...NUTRIENT_ROWS.map(({ nutrient }) => values[nutrient]),
    ].some((value) => value !== null)
  );
};

export const hasNutritionTable = (nutrition: NutritionEU) =>
  nutritionTableColumns(nutrition).length > 0;

// Ungrouped, so a German "1.397 kJ" can't be misread as a decimal.
const formatNumber = (
  value: number,
  language: string,
  maximumFractionDigits: number
) =>
  new Intl.NumberFormat(language, {
    maximumFractionDigits,
    useGrouping: false,
  }).format(value);

const formatRounded = (
  rounding: NutritionRounding | null,
  unit: string,
  language: string
) =>
  rounding === null
    ? MISSING
    : rounding.kind === "lessThan"
    ? `<${formatNumber(rounding.limit, language, 2)} ${unit}`
    : `${formatNumber(
        rounding.value,
        language,
        rounding.maximumFractionDigits
      )} ${unit}`;

export const formatEnergy = (
  value: number | null,
  unit: "kcal" | "kJ",
  language: string
) =>
  formatRounded(
    nutritionRounding(unit === "kcal" ? "energyKcal" : "energyKj", value),
    unit,
    language
  );

export const formatGrams = (
  nutrient: Nutrient,
  value: number | null,
  language: string
) => formatRounded(nutritionRounding(nutrient, value), "g", language);

export const formatPortionSize = (
  portionSize: number,
  unit: NutritionEUUnit,
  language: string
) => `${formatNumber(portionSize, language, 1)} ${unit}`;
